import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import { useTheme } from "@/contexts/ThemeContext";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { BottomNav } from "@/components/layout/BottomNav";
import {
  LayoutDashboard,
  Users,
  Target,
  Trophy,
  Award,
  Shuffle,
  Calendar,
  CheckCircle,
  DollarSign,
  BarChart3,
  Shield,
  Send,
  Mail,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  Sun,
  Moon,
  Key,
  Ban,
  UserCog,
} from "lucide-react";
import { useState, useEffect } from "react";

// ── Menu items organized in logical groups ──
const menuGroups = [
  {
    label: "Principal",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { id: "evaluate", label: "Avaliar", icon: Target, path: "/evaluate", accent: true },
      { id: "players", label: "Jogadores", icon: Users, path: "/players" },
      { id: "ranking", label: "Ranking", icon: Award, path: "/ranking" },
      { id: "statistics", label: "Estatísticas", icon: BarChart3, path: "/statistics" },
    ],
  },
  {
    label: "Gestão",
    items: [
      { id: "gameday", label: "Times do Dia", icon: Shuffle, path: "/gameday", roles: ["admin", "tecnico"] },
      { id: "game-registrations", label: "Inscrições", icon: CheckCircle, path: "/game-registrations", roles: ["admin", "tecnico"] },
      { id: "teams", label: "Times", icon: Trophy, path: "/teams", roles: ["admin", "tecnico"] },
      { id: "evaluators", label: "Avaliadores", icon: Shield, path: "/evaluators", roles: ["admin", "tecnico"] },
      { id: "evaluations_panel", label: "Painel de Avaliações", icon: BarChart3, path: "/evaluations", roles: ["admin", "tecnico"], permissionBased: true },
      { id: "payments", label: "Pagamentos", icon: DollarSign, path: "/payments", roles: ["admin", "tecnico"] },
    ],
  },
  {
    label: "Organização",
    items: [
      { id: "calendar", label: "Calendário", icon: Calendar, path: "/calendar" },
      { id: "attendance", label: "Frequência", icon: CheckCircle, path: "/attendance" },
    ],
  },
];

// Flat list for permission checks and backward compat
const menuItems = menuGroups.flatMap(g => g.items);

const adminItems = [
  { id: "admin-profile", label: "Meu Perfil", icon: UserCog, path: "/admin-profile", highlight: false },
  { id: "role-permissions", label: "Permissões", icon: Settings, path: "/role-permissions", highlight: true },
  { id: "evaluator-permissions", label: "Bloquear Jogadores", icon: Ban, path: "/permissions", highlight: true },
  { id: "password-management", label: "Senhas", icon: Key, path: "/password-management", highlight: true },
  { id: "invites", label: "Convites", icon: Send, path: "/invite-players" },
  { id: "reports", label: "Relatórios", icon: Mail, path: "/weekly-report" },
  { id: "player-report", label: "Relatório Jogadores", icon: BarChart3, path: "/player-report" },
];

const userItems = [
  { id: "change-password", label: "Alterar Senha", icon: Key, path: "/change-password" },
];

function SidebarButton({ item, active, collapsed, onClick }) {
  const Icon = item.icon;
  const baseActive = item.highlight
    ? "bg-red-600 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)]"
    : "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.3)]";
  const baseInactive = item.highlight
    ? "bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/30"
    : item.accent
      ? "bg-[hsl(var(--highlight))]/10 text-[hsl(var(--highlight))] hover:bg-[hsl(var(--highlight))]/20 border border-[hsl(var(--highlight))]/30"
      : "text-muted-foreground hover:text-foreground hover:bg-muted/50";

  return (
    <button
      key={item.id}
      onClick={onClick}
      data-testid={`sidebar-${item.id}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
        active ? baseActive : baseInactive
      } ${collapsed ? "justify-center" : ""}`}
    >
      <Icon
        className={`h-5 w-5 flex-shrink-0 ${!active && item.highlight ? "text-red-500" : ""} ${active ? "" : "group-hover:scale-110"} transition-transform`}
      />
      {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
    </button>
  );
}

/* ── Desktop Sidebar ──────────────────────────────── */
export function Sidebar({ evaluator, onLogout, collapsed, setCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [rolePermissions, setRolePermissions] = useState({});

  useEffect(() => {
    const fetchPermissions = async () => {
      if (evaluator?.role === "admin") return;
      try {
        const response = await axios.get(`${API}/role-permissions`);
        setRolePermissions(response.data);
      } catch (error) {
        console.error("Failed to fetch role permissions:", error);
      }
    };
    if (evaluator) fetchPermissions();
  }, [evaluator]);

  const hasPermission = (featureId) => {
    if (evaluator?.role === "admin") return true;
    const role = evaluator?.role || "avaliador";
    const permissions = rolePermissions[role];
    if (!permissions) {
      const defaultAvaliador = ["dashboard", "evaluate", "players", "ranking", "statistics", "calendar", "attendance"];
      const defaultTecnico = ["dashboard", "evaluate", "players", "ranking", "statistics", "calendar", "attendance", "gameday", "game-registrations", "teams", "evaluators", "payments"];
      if (role === "tecnico") return defaultTecnico.includes(featureId);
      return defaultAvaliador.includes(featureId);
    }
    return permissions[featureId] === true;
  };

  const isActive = (path) => location.pathname === path;

  const getPhotoUrl = () => {
    if (!evaluator?.linked_player_photo) return null;
    const photo = evaluator.linked_player_photo;
    return photo.startsWith("/") ? `${API.replace("/api", "")}${photo}` : photo;
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-50 transition-all duration-300 ${
        collapsed ? "w-20" : "w-64"
      } bg-card/95 backdrop-blur-xl border-r border-border flex flex-col`}
    >
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-center">
          <img
            src="/logo.png"
            alt="VolleyxBrasil"
            className={`${collapsed ? "w-14 h-14" : "w-24 h-24"} flex-shrink-0`}
          />
        </div>
      </div>

      {/* User Info */}
      {!collapsed && evaluator && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden border-2 border-primary/30">
              {evaluator.linked_player_photo ? (
                <img
                  src={getPhotoUrl()}
                  alt={evaluator.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <span
                className={`text-primary font-bold text-sm ${evaluator.linked_player_photo ? "hidden" : "flex"}`}
              >
                {evaluator.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{evaluator.name}</p>
              <Badge variant="outline" className="text-[10px] capitalize border-primary/50 text-primary">
                {evaluator.role || "avaliador"}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {menuGroups.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (item.roles && !item.roles.includes(evaluator?.role)) return false;
            return hasPermission(item.id);
          });
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label}>
              <p
                className={`text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5 mt-3 first:mt-0 ${collapsed ? "text-center" : "px-3"}`}
              >
                {collapsed ? "•" : group.label}
              </p>
              {visibleItems.map((item) => (
                <SidebarButton
                  key={item.id}
                  item={item}
                  active={isActive(item.path)}
                  collapsed={collapsed}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>
          );
        })}

        {evaluator?.role === "admin" && (
          <>
            <div className="pt-4 mt-4 border-t border-border">
              <p
                className={`text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 ${collapsed ? "text-center" : "px-3"}`}
              >
                {collapsed ? "•" : "Admin"}
              </p>
            </div>
            {adminItems.map((item) => (
              <SidebarButton
                key={item.id}
                item={item}
                active={isActive(item.path)}
                collapsed={collapsed}
                onClick={() => navigate(item.path)}
              />
            ))}
          </>
        )}

        {evaluator?.role && evaluator.role !== "admin" && (
          <>
            <div className="pt-4 mt-4 border-t border-border">
              <p
                className={`text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 ${collapsed ? "text-center" : "px-3"}`}
              >
                {collapsed ? "•" : "Minha Conta"}
              </p>
            </div>
            {userItems.map((item) => (
              <SidebarButton
                key={item.id}
                item={item}
                active={isActive(item.path)}
                collapsed={collapsed}
                onClick={() => navigate(item.path)}
              />
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-border space-y-2 mt-auto">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          {!collapsed && <span className="text-sm">Recolher</span>}
        </button>

        <button
          onClick={() => toggleTheme()}
          data-testid="theme-toggle"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
        >
          {theme === "dark" ? (
            <Sun className="h-5 w-5 flex-shrink-0 text-yellow-400" />
          ) : (
            <Moon className="h-5 w-5 flex-shrink-0 text-blue-400" />
          )}
          {!collapsed && (
            <span className="text-sm font-medium">{theme === "dark" ? "Tema Claro" : "Tema Escuro"}</span>
          )}
        </button>

        <button
          onClick={onLogout}
          data-testid="sidebar-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Sair</span>}
        </button>

        {!collapsed && (
          <p className="text-[10px] text-center text-muted-foreground pt-2">
            por <span className="text-muted-foreground/80">Jhonatas Valim</span>
          </p>
        )}
      </div>
    </aside>
  );
}

/* ── Mobile Drawer (Sheet) ─────────────────────────── */
function MobileDrawer({ open, onOpenChange, evaluator, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [rolePermissions, setRolePermissions] = useState({});

  useEffect(() => {
    const fetchPermissions = async () => {
      if (evaluator?.role === "admin") return;
      try {
        const response = await axios.get(`${API}/role-permissions`);
        setRolePermissions(response.data);
      } catch (error) {
        console.error("Failed to fetch role permissions:", error);
      }
    };
    if (evaluator) fetchPermissions();
  }, [evaluator]);

  const hasPermission = (featureId) => {
    if (evaluator?.role === "admin") return true;
    const role = evaluator?.role || "avaliador";
    const permissions = rolePermissions[role];
    if (!permissions) {
      const defaultAvaliador = ["dashboard", "evaluate", "players", "ranking", "statistics", "calendar", "attendance"];
      const defaultTecnico = ["dashboard", "evaluate", "players", "ranking", "statistics", "calendar", "attendance", "gameday", "game-registrations", "teams", "evaluators", "payments"];
      if (role === "tecnico") return defaultTecnico.includes(featureId);
      return defaultAvaliador.includes(featureId);
    }
    return permissions[featureId] === true;
  };

  const isActive = (path) => location.pathname === path;

  const go = (path) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-72 p-0 bg-card border-border flex flex-col">
        <SheetHeader className="sr-only">
          <SheetTitle>Menu de Navegação</SheetTitle>
          <SheetDescription>Navegue pelas páginas do sistema</SheetDescription>
        </SheetHeader>

        {/* Logo + User */}
        <div className="px-4 pt-5 pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="VolleyxBrasil" className="w-10 h-10" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {evaluator?.name}
              </p>
              <Badge variant="outline" className="text-[10px] capitalize border-primary/50 text-primary">
                {evaluator?.role || "avaliador"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {menuGroups.map((group) => {
            const visibleItems = group.items.filter((item) => {
              if (item.roles && !item.roles.includes(evaluator?.role)) return false;
              return hasPermission(item.id);
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.label}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5 mt-3 first:mt-0 px-3">
                  {group.label}
                </p>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.id}
                      onClick={() => go(item.path)}
                      data-testid={`drawer-${item.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                        active
                          ? "bg-primary text-primary-foreground"
                          : item.accent
                            ? "text-[hsl(var(--highlight))] bg-[hsl(var(--highlight))]/10 border border-[hsl(var(--highlight))]/20"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Admin section */}
          {evaluator?.role === "admin" && (
            <>
              <div className="pt-3 mt-3 border-t border-border">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 px-3">
                  Admin
                </p>
              </div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.path)}
                    data-testid={`drawer-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                      active
                        ? item.highlight
                          ? "bg-red-600 text-white"
                          : "bg-primary text-primary-foreground"
                        : item.highlight
                          ? "text-red-500 bg-red-500/10 border border-red-500/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Non-admin account section */}
          {evaluator?.role && evaluator.role !== "admin" && (
            <>
              <div className="pt-3 mt-3 border-t border-border">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 px-3">
                  Minha Conta
                </p>
              </div>
              {userItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.path)}
                    data-testid={`drawer-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-border space-y-1.5">
          <button
            onClick={() => toggleTheme()}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5 text-yellow-400" />
            ) : (
              <Moon className="h-5 w-5 text-blue-400" />
            )}
            <span className="text-sm font-medium">
              {theme === "dark" ? "Tema Claro" : "Tema Escuro"}
            </span>
          </button>
          <button
            onClick={() => {
              onLogout();
              onOpenChange(false);
            }}
            data-testid="drawer-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-medium">Sair</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ── PageLayout ────────────────────────────────────── */
export function PageLayout({ children, evaluator, onLogout, title, subtitle }) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar
          evaluator={evaluator}
          onLogout={onLogout}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        evaluator={evaluator}
        onLogout={onLogout}
      />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-40 backdrop-blur-xl bg-card/90 border-b border-border flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 text-foreground active:scale-95 transition-transform"
          >
            <Menu className="h-5 w-5" />
          </button>
          {title && (
            <h1
              className="text-sm font-bold uppercase tracking-tight text-foreground truncate"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {title}
            </h1>
          )}
        </header>

        <main
          className={`transition-all duration-300 ${collapsed ? "md:ml-20" : "md:ml-64"}`}
        >
          {/* Desktop header */}
          {title && (
            <header className="hidden md:block sticky top-0 z-40 backdrop-blur-xl bg-card/80 border-b border-border">
              <div className="px-8 py-6">
                <h1
                  className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-foreground"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                )}
              </div>
            </header>
          )}

          {/* Page content — extra bottom padding on mobile for BottomNav */}
          <div className="p-4 md:p-8 pb-20 md:pb-8">{children}</div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNav onOpenMenu={() => setDrawerOpen(true)} />
    </div>
  );
}
